import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import { api, ClientApiError, type ClientDeposit } from '../api'
import { tradingServerLabel } from '../branding'
import { fixtureAccount, useFixturesFromQuery } from '../fixtures'
import { useAppPath } from '../nav'
import type { TradingAccount } from '../types'
import { money } from '../prediction-filter'
import { Button } from '../../components/ui/Button'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { LiveRequiredPanel } from '../components/LiveRequiredPanel'

type DepositMethod = 'uniwire' | null
type DepositProvider = {
  provider: string
  asset?: string
  network?: string
  wallet_address?: string
  minimum_usd?: string | number
  maximum_usd?: string | number
  warning?: string
  automatic_confirmation?: boolean
}

type TimelineEvent = {
  event_type?: string
  timestamp?: string
  description?: string
}

type StatusTone =
  | 'awaiting'
  | 'pending'
  | 'confirmed'
  | 'processing'
  | 'completed'
  | 'rejected'
  | 'failed'
  | 'expired'
  | 'recon'
  | 'neutral'

function safeError(err: unknown, fallback: string): string {
  if (err instanceof ClientApiError) {
    const msg = (err.message || '').trim()
    if (!msg) return fallback
    const lower = msg.toLowerCase()
    if (
      lower.includes('internal') ||
      lower.includes('exception') ||
      lower.includes('traceback') ||
      lower === 'invalid request' ||
      lower.includes('stack') ||
      lower.includes('mt5manager') ||
      lower.includes('pip install') ||
      lower.includes('\\downloads\\') ||
      lower.includes('/downloads/') ||
      lower.includes('.whl') ||
      lower.includes('package is not installed') ||
      lower.includes('metaquotes') ||
      lower.includes('account creation disabled') ||
      lower.includes('mt5_allow_account_creation')
    ) {
      return fallback
    }
    return msg
  }
  if (err instanceof Error && err.message) {
    const lower = err.message.toLowerCase()
    if (
      lower.includes('internal') ||
      lower.includes('failed to fetch') ||
      lower.includes('mt5manager') ||
      lower.includes('pip install') ||
      lower.includes('package is not installed')
    ) {
      return fallback
    }
    return err.message
  }
  return fallback
}

function isSelectableAccount(account: TradingAccount): boolean {
  const status = String(account.status || '').toUpperCase()
  return status === 'ACTIVE' || status === ''
}

function maskLogin(login: number | string | null | undefined): string {
  const text = String(login ?? '').trim()
  if (!text) return '—'
  if (text.length <= 2) return `**${text}`
  return `***${text.slice(-2)}`
}

function statusTone(raw?: string | null): StatusTone {
  const s = String(raw || '').toUpperCase()
  if (!s) return 'neutral'
  if (s.includes('RECONCIL')) return 'recon'
  if (s.includes('EXPIRED')) return 'expired'
  if (/(REJECT|DENIED)/.test(s)) return 'rejected'
  if (/(FAIL|CANCEL)/.test(s)) return 'failed'
  if (/(COMPLETE|CREDIT|SUCCESS|SETTLED)/.test(s) && !s.includes('PROCESSING')) return 'completed'
  if (s.includes('PAYMENT_CONFIRMED') || (s.includes('CONFIRM') && !s.includes('AWAIT'))) return 'confirmed'
  if (/(PROCESS|REVIEW)/.test(s)) return 'processing'
  if (/(AWAIT|INVOICE)/.test(s)) return 'awaiting'
  if (/(PEND|SUBMIT|CREATED|OPEN)/.test(s)) return 'pending'
  return 'pending'
}

function statusLabel(raw?: string | null): string {
  const s = String(raw || '').toUpperCase()
  if (!s) return '—'
  const map: Record<string, string> = {
    AWAITING_PAYMENT: 'Awaiting Payment',
    PENDING: 'Pending',
    PAYMENT_CONFIRMED: 'Payment Confirmed',
    PAYMENT_PENDING: 'Awaiting Payment',
    PROCESSING: 'Processing',
    PROCESSING_MT5_CREDIT: 'Processing',
    COMPLETED: 'Completed',
    REJECTED: 'Rejected',
    FAILED: 'Failed',
    EXPIRED: 'Expired',
    RECONCILIATION_REQUIRED: 'Reconciliation Required',
  }
  if (map[s]) return map[s]
  return s
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

function methodLabel(provider?: string | null): string {
  const p = String(provider || '').toUpperCase()
  if (p.includes('UNIWIRE')) return 'Uniwire'
  if (p.includes('MANUAL') || p.includes('DEMO')) return 'Manual Demo'
  return provider || '—'
}

function StatusBadge({ status }: { status?: string | null }) {
  return <span className={`cp-status cp-status--${statusTone(status)}`}>{statusLabel(status)}</span>
}

async function copyText(value: string) {
  await navigator.clipboard.writeText(value)
}

/** Uniwire may return USDT-TRX / USDTTRC20 — show a client-friendly ticker. */
function cryptoDisplayCurrency(raw?: string | null): string {
  const c = String(raw || 'USDT').trim().toUpperCase()
  if (c === 'USDT-TRX' || c === 'USDTTRC20' || c === 'USDT_TRC20') return 'USDT'
  return c || 'USDT'
}

function isAwaitingPaymentStatus(raw?: string | null): boolean {
  const s = String(raw || '').toUpperCase()
  return (
    s.includes('AWAIT') ||
    s === 'PAYMENT_PENDING' ||
    s === 'PENDING' ||
    s.includes('CREATING')
  )
}

function isCreditingStatus(raw?: string | null): boolean {
  const s = String(raw || '').toUpperCase()
  return s === 'PAYMENT_CONFIRMED' || s === 'PROCESSING_MT5_CREDIT' || s === 'PROCESSING'
}

/** Keep polling until COMPLETED (or a terminal failure). */
function isWaitingStatus(raw?: string | null): boolean {
  const s = String(raw || '').toUpperCase()
  if (!s) return false
  if (s === 'COMPLETED') return false
  if (/(COMPLETE|SUCCESS|SETTLED)/.test(s) && !s.includes('PROCESSING') && !s.includes('INCOMPLETE')) {
    return false
  }
  if (/(FAIL|REJECT|DENIED|EXPIRED|CANCEL|RECONCIL|OVERPAID|UNDERPAID|INCOMPLETE)/.test(s)) {
    return false
  }
  return isAwaitingPaymentStatus(s) || isCreditingStatus(s)
}

function successNoticeForStatus(raw?: string | null): string {
  const s = String(raw || '').toUpperCase()
  if (s === 'COMPLETED' || (s.includes('COMPLETE') && !s.includes('PROCESSING') && !s.includes('INCOMPLETE'))) {
    return 'Payment successful. Funds have been credited to your Live MT5 account.'
  }
  if (s === 'PAYMENT_CONFIRMED' || s === 'PROCESSING_MT5_CREDIT' || s.includes('PROCESSING')) {
    return 'Payment received. Crediting your Live MT5 account…'
  }
  if (s.includes('CONFIRM')) {
    return 'Payment received. Crediting your Live MT5 account…'
  }
  return `Deposit status: ${statusLabel(s)}`
}

function formatCountdown(ms: number): string {
  if (ms <= 0) return '00:00'
  const totalSec = Math.floor(ms / 1000)
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

export function DepositsPage() {
  const appPath = useAppPath()
  const fixtures = useFixturesFromQuery()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState(false)
  const [method, setMethod] = useState<DepositMethod>(null)

  const [accounts, setAccounts] = useState<TradingAccount[]>([])
  const [accountId, setAccountId] = useState<number | ''>('')
  const [providers, setProviders] = useState<DepositProvider[]>([])
  const [deposits, setDeposits] = useState<ClientDeposit[]>([])

  const [uniwireAmount, setUniwireAmount] = useState('20')
  const [invoice, setInvoice] = useState<(ClientDeposit & Record<string, unknown>) | null>(null)
  const [copied, setCopied] = useState(false)
  const [confirmKind, setConfirmKind] = useState<'uniwire' | null>(null)
  const [nowMs, setNowMs] = useState(() => Date.now())

  const [detailId, setDetailId] = useState<number | null>(null)
  const [detail, setDetail] = useState<(ClientDeposit & Record<string, unknown>) | null>(null)
  const [timeline, setTimeline] = useState<TimelineEvent[]>([])
  const [detailWarning, setDetailWarning] = useState('')
  const [detailLoading, setDetailLoading] = useState(false)

  const selectable = useMemo(() => accounts.filter(isSelectableAccount), [accounts])
  const liveSelectable = useMemo(
    () =>
      selectable.filter((a) => {
        const m = String(a.mode || '').toUpperCase()
        return m === 'LIVE' || m === 'REAL'
      }),
    [selectable],
  )
  const selected = liveSelectable.find((a) => a.id === accountId) || null
  const hasAccount = selectable.length > 0
  const hasLiveAccount = liveSelectable.length > 0

  const uniwireProvider = providers.find((p) => String(p.provider).toUpperCase() === 'UNIWIRE')

  const load = useCallback(async () => {
    if (fixtures) {
      setAccounts([fixtureAccount])
      setAccountId(fixtureAccount.id)
      // Fixtures stay Demo-only so Live-required deposit state can be verified.
      setProviders([
        {
          provider: 'UNIWIRE',
          asset: 'USDT',
          network: 'TRC20',
          automatic_confirmation: true,
          minimum_usd: 20,
          maximum_usd: 5000,
        },
        {
          provider: 'MANUAL_DEMO',
          asset: 'USDT',
          network: 'TRC20',
          minimum_usd: 20,
          maximum_usd: 500,
        },
      ])
      setDeposits([
        {
          id: 11,
          reference: 'DEP-A1B2-C3',
          amount: '20.00',
          currency: 'USD',
          status: 'COMPLETED',
          provider: 'MANUAL_DEMO',
          created_at: '2026-08-04T12:00:00Z',
          completed_at: '2026-08-04T12:10:00Z',
          mt5_login_masked: '***56',
          asset: 'USDT',
          network: 'TRC20',
        },
        {
          id: 12,
          reference: 'UW-9F8E-7D6C',
          amount: '50.00',
          currency: 'USD',
          status: 'PROCESSING',
          provider: 'UNIWIRE',
          created_at: '2026-08-05T09:30:00Z',
          mt5_login_masked: '***56',
          asset: 'USDT',
          network: 'TRC20',
        },
      ])
      return
    }
    const [listed, deps, cfg] = await Promise.all([
      api.listAccounts(),
      api.listDeposits().catch(() => ({ deposits: [] as ClientDeposit[] })),
      api.depositConfig().catch(() => null),
    ])
    const all = listed.accounts || []
    const active = all.filter(isSelectableAccount)
    const liveActive = active.filter((a) => {
      const m = String(a.mode || '').toUpperCase()
      return m === 'LIVE' || m === 'REAL'
    })
    setAccounts(all)
    setDeposits(deps.deposits || [])
    setProviders((cfg?.providers as DepositProvider[] | undefined) || [])

    setAccountId((prev) => {
      if (prev && liveActive.some((a) => a.id === prev)) return prev
      return liveActive[0]?.id ?? ''
    })

  }, [fixtures])

  useEffect(() => {
    let cancelled = false
    async function run() {
      setLoading(true)
      setError('')
      try {
        await load()
      } catch (err) {
        if (!cancelled) setError(safeError(err, 'Unable to load deposit information.'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [load])

  const invoiceStatus = String(invoice?.status || '')
  const invoiceAwaitingPayment = Boolean(invoice && isAwaitingPaymentStatus(invoiceStatus))
  const invoiceCrediting = Boolean(invoice && isCreditingStatus(invoiceStatus))
  const invoiceWaiting = Boolean(invoice && isWaitingStatus(invoiceStatus))
  const invoiceExpiresAt = invoice
    ? String(invoice.expires_at || invoice.invoice_expires_at || '')
    : ''
  const expiresMs = invoiceExpiresAt ? Date.parse(invoiceExpiresAt) : NaN
  const countdownLabel = Number.isFinite(expiresMs)
    ? formatCountdown(expiresMs - nowMs)
    : null

  useEffect(() => {
    if (!invoiceAwaitingPayment) return
    const tick = window.setInterval(() => setNowMs(Date.now()), 1000)
    return () => window.clearInterval(tick)
  }, [invoiceAwaitingPayment])

  useEffect(() => {
    if (!invoiceWaiting || !invoice?.id) return
    let cancelled = false
    let refreshInFlight = false
    const depositId = Number(invoice.id)

    async function applyDeposit(dep: ClientDeposit & Record<string, unknown>) {
      setInvoice(dep)
      const next = String(dep.status || '').toUpperCase()
      if (!isWaitingStatus(next)) {
        setNotice(successNoticeForStatus(next))
        await load()
      } else if (next === 'PAYMENT_CONFIRMED' || next === 'PROCESSING_MT5_CREDIT') {
        setNotice(successNoticeForStatus(next))
      }
    }

    async function pollLocalStatus() {
      if (cancelled || busy) return
      try {
        const res = await api.getDeposit(depositId)
        if (cancelled || !res.deposit) return
        await applyDeposit(res.deposit as ClientDeposit & Record<string, unknown>)
      } catch {
        /* keep polling */
      }
    }

    async function pollProviderRefresh() {
      if (cancelled || busy || refreshInFlight) return
      refreshInFlight = true
      try {
        const res = await api.refreshUniwireDeposit(depositId)
        if (cancelled || !res.deposit) return
        await applyDeposit(res.deposit as ClientDeposit & Record<string, unknown>)
      } catch {
        /* keep polling; local status poll still runs */
      } finally {
        refreshInFlight = false
      }
    }

    // Immediate checks so success appears as soon as webhook/poller credits MT5.
    void pollLocalStatus()
    void pollProviderRefresh()
    const localPoll = window.setInterval(() => {
      void pollLocalStatus()
    }, 2000)
    const providerPoll = window.setInterval(() => {
      void pollProviderRefresh()
    }, 6000)
    return () => {
      cancelled = true
      window.clearInterval(localPoll)
      window.clearInterval(providerPoll)
    }
  }, [invoiceWaiting, invoice?.id, busy, load])

  async function submitUniwire(e: FormEvent) {
    e.preventDefault()
    if (!accountId) {
      setError('Select an active trading account.')
      return
    }
    if (!uniwireProvider) {
      setError('Crypto deposits are temporarily unavailable.')
      return
    }
    setConfirmKind('uniwire')
  }

  async function executeUniwireInvoice() {
    if (!uniwireProvider || !accountId) return
    setBusy(true)
    setError('')
    setNotice('')
    setInvoice(null)
    try {
      const res = await api.createUniwireDeposit({
        tradingAccountId: Number(accountId),
        amountUsd: uniwireAmount,
        asset: uniwireProvider.asset || 'USDT',
        network: uniwireProvider.network || 'TRC20',
      })
      setConfirmKind(null)
      setInvoice((res.deposit || null) as ClientDeposit & Record<string, unknown>)
      setNotice('Deposit invoice created. Complete payment using the address below.')
      await load()
      // Move focus to the generated invoice panel.
      window.requestAnimationFrame(() => {
        document.getElementById('deposit-invoice-panel')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      })
    } catch (err) {
      setError(safeError(err, 'Unable to create deposit invoice.'))
      setConfirmKind(null)
    } finally {
      setBusy(false)
    }
  }

  async function refreshInvoice() {
    if (!invoice?.id) return
    setBusy(true)
    setError('')
    try {
      const res = await api.refreshUniwireDeposit(Number(invoice.id))
      if (res.deposit) {
        const dep = res.deposit as ClientDeposit & Record<string, unknown>
        setInvoice(dep)
        const next = String(dep.status || '').toUpperCase()
        if (!isWaitingStatus(next) || next === 'PAYMENT_CONFIRMED' || next === 'PROCESSING_MT5_CREDIT') {
          setNotice(successNoticeForStatus(next))
        }
      }
      await load()
    } catch (err) {
      setError(safeError(err, 'Unable to refresh deposit status.'))
    } finally {
      setBusy(false)
    }
  }

  async function openDetail(id: number) {
    setDetailId(id)
    setDetail(null)
    setTimeline([])
    setDetailWarning('')
    setDetailLoading(true)
    setError('')
    try {
      const res = await api.getDeposit(id)
      setDetail((res.deposit || null) as ClientDeposit & Record<string, unknown>)
      setTimeline(res.timeline || [])
      setDetailWarning(res.warning || '')
    } catch (err) {
      setError(safeError(err, 'Unable to load deposit information.'))
      setDetailId(null)
    } finally {
      setDetailLoading(false)
    }
  }

  const quotedCryptoAmount = invoice
    ? String(invoice.quoted_crypto_amount || '—')
    : '—'
  const quotedCryptoCurrency = invoice
    ? cryptoDisplayCurrency(
        String(invoice.quoted_crypto_currency || uniwireProvider?.asset || 'USDT'),
      )
    : 'USDT'

  return (
    <section className="cp-page cp-deposits">
      <header className="cp-deposits__page-head">
        <h1>Deposit Funds</h1>
        <p className="cp-deposits__trust">Protected and recorded through Perfect Predict</p>
      </header>

      {loading ? (
        <p className="cp-muted ap-inline-status" aria-live="polite">
          Loading…
        </p>
      ) : null}
      {error ? (
        <div className="cp-deposits__alert cp-deposits__alert--error" role="alert">
          <p>{error}</p>
          <Button type="button" variant="ghost" size="sm" onClick={() => void load()}>
            Retry
          </Button>
        </div>
      ) : null}
      {notice ? <p className="cp-ok">{notice}</p> : null}

      {!loading && !hasAccount ? (
        <div className="cp-deposits__blocked">
          <span className="cp-badge cp-badge--mt5">Powered by MT5</span>
          <h2>Trading account required</h2>
          <p>
            Open a Demo account to practice, or create a Live account under Trading Accounts to fund
            with real money.
          </p>
          <div className="cp-deposits__blocked-actions">
            <Button to={appPath('/app/accounts')} variant="primary">
              View Trading Accounts
            </Button>
          </div>
        </div>
      ) : null}

      {!loading && hasAccount && !hasLiveAccount ? (
        <LiveRequiredPanel kind="deposit" />
      ) : null}

      {loading || hasLiveAccount ? (
        <>
          {!loading && liveSelectable.length > 1 ? (
            <label className="cp-deposits__account-select cp-workspace-select">
              <span>Trading account</span>
              <select
                value={accountId}
                onChange={(e) => setAccountId(e.target.value ? Number(e.target.value) : '')}
              >
                {liveSelectable.map((a) => (
                  <option key={a.id} value={a.id}>
                    MT5 {maskLogin(a.mt5Login)} · {tradingServerLabel(a.mode, a.server)}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          {!loading && !method ? (
            <section className="cp-deposits__methods" aria-labelledby="deposit-methods-heading">
              <div className="cp-deposits__section-head">
                <h2 id="deposit-methods-heading">Choose Deposit Method</h2>
                <p className="cp-muted">Select how you want to fund your MT5 account.</p>
              </div>

              <div className="cp-deposits__grid">
                <article className="cp-method-card cp-method-card--crypto">
                  <div className="cp-method-card__top">
                    <span className="cp-method-card__mark" aria-hidden="true" />
                    <p className="cp-method-card__powered">Powered by Uniwire</p>
                    <h3>Crypto Deposit</h3>
                    <p className="cp-method-card__sub">
                      Create a secure USDT TRC20 payment invoice and track its confirmation status.
                    </p>
                  </div>
                  <ul className="cp-method-card__list">
                    <li>Asset: USDT</li>
                    <li>Network: TRC20</li>
                    <li>Automatic provider confirmation</li>
                    <li>Deposit status tracking</li>
                    <li>Secure invoice/payment address</li>
                  </ul>
                  {uniwireProvider ? (
                    <Button
                      type="button"
                      variant="primary"
                      className="cp-method-card__cta"
                      onClick={() => {
                        setMethod('uniwire')
                        setInvoice(null)
                        setNotice('')
                        setError('')
                      }}
                    >
                      Continue with Uniwire
                    </Button>
                  ) : (
                    <button type="button" className="cp-method-card__cta cp-method-card__cta--soon" disabled>
                      Temporarily Unavailable
                    </button>
                  )}
                </article>
              </div>
            </section>
          ) : loading ? (
            <section
              className="cp-deposits__methods is-refreshing"
              aria-labelledby="deposit-methods-heading"
            >
              <div className="cp-deposits__section-head">
                <h2 id="deposit-methods-heading">Choose Deposit Method</h2>
                <p className="cp-muted">Select how you want to fund your MT5 account.</p>
              </div>
            </section>
          ) : null}

          {!loading && method === 'uniwire' && uniwireProvider ? (
            <section className="cp-deposits__panel" aria-labelledby="uniwire-form-heading">
              <div className="cp-deposits__panel-head">
                <div>
                  <h2 id="uniwire-form-heading">Crypto Deposit</h2>
                  <p className="cp-muted">USDT · TRC20 · Powered by Uniwire</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setMethod(null)
                    setInvoice(null)
                  }}
                >
                  Change Method
                </Button>
              </div>

              {!invoice ? (
                <form className="cp-deposits__form" onSubmit={(e) => void submitUniwire(e)}>
                  <label className="cp-field">
                    Trading account
                    <select
                      value={accountId}
                      onChange={(e) => setAccountId(e.target.value ? Number(e.target.value) : '')}
                      required
                    >
                      {liveSelectable.map((a) => (
                        <option key={a.id} value={a.id}>
                          MT5 {maskLogin(a.mt5Login)} · {tradingServerLabel(a.mode, a.server)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="cp-field">
                    Amount (USD)
                    <input
                      value={uniwireAmount}
                      onChange={(e) => setUniwireAmount(e.target.value)}
                      inputMode="decimal"
                      required
                    />
                  </label>
                  <p className="cp-note">
                    Asset {(uniwireProvider.asset || 'USDT').toUpperCase()} · Network{' '}
                    {(uniwireProvider.network || 'TRC20').toUpperCase()}
                  </p>
                  <Button type="submit" variant="primary" disabled={busy}>
                    Create Deposit Invoice
                  </Button>
                </form>
              ) : (
                <div className="cp-deposits__invoice" id="deposit-invoice-panel" tabIndex={-1}>
                  <div className="cp-deposits__invoice-top">
                    <h3>Payment details</h3>
                    {invoiceAwaitingPayment ? (
                      <div className="cp-deposits__wait" role="status" aria-live="polite">
                        <span className="cp-deposits__wait-dot" aria-hidden="true" />
                        <span className="cp-deposits__wait-text">Waiting for confirmation</span>
                        {countdownLabel ? (
                          <span className="cp-deposits__timer" title="Time left to pay">
                            {Number.isFinite(expiresMs) && expiresMs - nowMs <= 0
                              ? 'Expired'
                              : countdownLabel}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                    {invoiceCrediting ? (
                      <div
                        className="cp-deposits__wait cp-deposits__wait--crediting"
                        role="status"
                        aria-live="polite"
                      >
                        <span className="cp-deposits__wait-dot" aria-hidden="true" />
                        <span className="cp-deposits__wait-text">Crediting Live MT5…</span>
                      </div>
                    ) : null}
                  </div>
                  <div className="cp-deposits__invoice-layout">
                    <div className="cp-deposits__invoice-main">
                      <dl>
                        <div>
                          <dt>Reference</dt>
                          <dd>{String(invoice.reference || invoice.id || '—')}</dd>
                        </div>
                        <div>
                          <dt>Status</dt>
                          <dd>
                            <StatusBadge status={String(invoice.status || '')} />
                          </dd>
                        </div>
                        <div>
                          <dt>Requested USD</dt>
                          <dd>
                            {String(invoice.requested_fiat_amount || invoice.amount || '—')}{' '}
                            {String(invoice.requested_fiat_currency || 'USD')}
                          </dd>
                        </div>
                        <div>
                          <dt>USDT TRC20 address</dt>
                          <dd className="cp-deposits__address-row">
                            <span className="cp-deposits__mono">
                              {String(invoice.payment_address || '—')}
                            </span>
                            {invoice.payment_address ? (
                              <button
                                type="button"
                                className="cp-deposits__copy-icon"
                                aria-label={copied ? 'Address copied' : 'Copy deposit address'}
                                title={copied ? 'Copied' : 'Copy address'}
                                onClick={() => {
                                  void copyText(String(invoice.payment_address || '')).then(() => {
                                    setCopied(true)
                                    window.setTimeout(() => setCopied(false), 1600)
                                  })
                                }}
                              >
                                {copied ? (
                                  <span className="cp-deposits__copy-check" aria-hidden="true">
                                    ✓
                                  </span>
                                ) : (
                                  <svg
                                    viewBox="0 0 24 24"
                                    width="16"
                                    height="16"
                                    aria-hidden="true"
                                    focusable="false"
                                  >
                                    <path
                                      fill="currentColor"
                                      d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"
                                    />
                                  </svg>
                                )}
                              </button>
                            ) : null}
                          </dd>
                        </div>
                        {invoiceAwaitingPayment ? (
                          <div>
                            <dt>Expires</dt>
                            <dd>
                              {invoice.expires_at || invoice.invoice_expires_at
                                ? new Date(
                                    String(invoice.expires_at || invoice.invoice_expires_at),
                                  ).toLocaleString()
                                : '—'}
                            </dd>
                          </div>
                        ) : null}
                      </dl>
                      <div className="cp-deposits__invoice-actions">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={busy}
                          onClick={() => void refreshInvoice()}
                        >
                          {busy ? 'Refreshing…' : 'Refresh status'}
                        </Button>
                        {invoiceAwaitingPayment &&
                        typeof invoice.payment_url === 'string' &&
                        invoice.payment_url ? (
                          <a
                            className="pp-btn pp-btn--secondary pp-btn--sm"
                            href={invoice.payment_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Open payment page
                          </a>
                        ) : null}
                      </div>
                    </div>
                    {invoiceAwaitingPayment ? (
                      <aside className="cp-deposits__invoice-side" aria-label="Pay with crypto">
                        <div className="cp-deposits__pay-amount">
                          <span className="cp-deposits__pay-label">Crypto amount</span>
                          <strong className="cp-deposits__pay-value">
                            {quotedCryptoAmount} {quotedCryptoCurrency}
                          </strong>
                          <span className="cp-deposits__pay-network">Network · TRC20 (Tron)</span>
                        </div>
                        {typeof invoice.qr_url === 'string' && invoice.qr_url ? (
                          <img
                            className="cp-deposits__qr"
                            src={invoice.qr_url}
                            alt="Deposit payment QR code"
                          />
                        ) : (
                          <p className="cp-muted cp-deposits__qr-fallback">
                            QR unavailable — use the copy icon next to the address.
                          </p>
                        )}
                      </aside>
                    ) : (
                      <aside
                        className="cp-deposits__invoice-side cp-deposits__invoice-side--done"
                        aria-label="Deposit progress"
                      >
                        <div className="cp-deposits__pay-amount">
                          <span className="cp-deposits__pay-label">Amount</span>
                          <strong className="cp-deposits__pay-value">
                            {String(invoice.requested_fiat_amount || invoice.amount || '—')} USD
                          </strong>
                          <span className="cp-deposits__pay-network">
                            {invoiceCrediting
                              ? 'Payment confirmed — crediting Live MT5'
                              : String(invoice.status || '').toUpperCase() === 'COMPLETED'
                                ? 'Credited to Live MT5'
                                : statusLabel(String(invoice.status || ''))}
                          </span>
                        </div>
                      </aside>
                    )}
                  </div>
                </div>
              )}
            </section>
          ) : null}

          <section
            className={`cp-deposits__history${loading ? ' is-refreshing' : ''}`}
            aria-labelledby="recent-deposits-heading"
          >
            <div className="cp-deposits__section-head">
              <h2 id="recent-deposits-heading">Recent Deposits</h2>
              <p className="cp-muted">Live history from your Perfect Predict deposit records.</p>
            </div>

            {!loading && deposits.length > 0 ? (
              <>
                <div className="cp-deposits__table-wrap">
                  <table className="cp-deposits__table">
                    <thead>
                      <tr>
                        <th>Reference</th>
                        <th>Account</th>
                        <th>Provider / Method</th>
                        <th>Amount</th>
                        <th>Asset / Network</th>
                        <th>Status</th>
                        <th>Submitted</th>
                        <th>Completed</th>
                        <th>View</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deposits.map((d) => (
                        <tr key={d.id}>
                          <td>{d.reference || `#${d.id}`}</td>
                          <td>{d.mt5_login_masked || '—'}</td>
                          <td>{methodLabel(d.provider)}</td>
                          <td>
                            {money(d.amount)} {d.currency || 'USD'}
                          </td>
                          <td>
                            {(d.asset || 'USDT').toUpperCase()} / {(d.network || 'TRC20').toUpperCase()}
                          </td>
                          <td>
                            <StatusBadge status={d.status} />
                          </td>
                          <td>{d.created_at ? new Date(d.created_at).toLocaleString() : '—'}</td>
                          <td>{d.completed_at ? new Date(d.completed_at).toLocaleString() : '—'}</td>
                          <td>
                            <button
                              type="button"
                              className="cp-deposits__link"
                              onClick={() => void openDetail(d.id)}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="cp-deposits__cards">
                  {deposits.map((d) => (
                    <article key={d.id} className="cp-deposit-card">
                      <div className="cp-deposit-card__top">
                        <strong>{d.reference || `#${d.id}`}</strong>
                        <StatusBadge status={d.status} />
                      </div>
                      <dl>
                        <div>
                          <dt>Amount</dt>
                          <dd>
                            {money(d.amount)} {d.currency || 'USD'}
                          </dd>
                        </div>
                        <div>
                          <dt>Method</dt>
                          <dd>{methodLabel(d.provider)}</dd>
                        </div>
                        <div>
                          <dt>Account</dt>
                          <dd>{d.mt5_login_masked || '—'}</dd>
                        </div>
                        <div>
                          <dt>Date</dt>
                          <dd>{d.created_at ? new Date(d.created_at).toLocaleString() : '—'}</dd>
                        </div>
                      </dl>
                      <button type="button" className="pp-btn pp-btn--secondary pp-btn--sm" onClick={() => void openDetail(d.id)}>
                        View Details
                      </button>
                    </article>
                  ))}
                </div>
              </>
            ) : !loading ? (
              <p className="cp-muted">No deposits yet.</p>
            ) : null}
          </section>
        </>
      ) : null}

      {detailId !== null ? (
        <div className="cp-deposits__drawer" role="dialog" aria-modal="true" aria-labelledby="deposit-detail-heading">
          <button
            type="button"
            className="cp-deposits__drawer-backdrop"
            aria-label="Close deposit details"
            onClick={() => setDetailId(null)}
          />
          <div className="cp-deposits__drawer-panel">
            <div className="cp-deposits__panel-head">
              <h2 id="deposit-detail-heading">Deposit Details</h2>
              <Button type="button" variant="ghost" size="sm" onClick={() => setDetailId(null)}>
                Close
              </Button>
            </div>
            {detailLoading ? <p className="cp-muted">Loading…</p> : null}
            {detail ? (
              <>
                {detailWarning ? <p className="cp-note">{detailWarning}</p> : null}
                <dl className="cp-deposits__detail-grid">
                  <div>
                    <dt>Reference</dt>
                    <dd>{String(detail.reference || detail.id)}</dd>
                  </div>
                  <div>
                    <dt>Trading account</dt>
                    <dd>{String(detail.mt5_login_masked || detail.trading_account_id || '—')}</dd>
                  </div>
                  <div>
                    <dt>Provider</dt>
                    <dd>{methodLabel(String(detail.provider || ''))}</dd>
                  </div>
                  <div>
                    <dt>Amount</dt>
                    <dd>
                      {money(detail.amount as string | number)} {String(detail.currency || 'USD')}
                    </dd>
                  </div>
                  <div>
                    <dt>Asset</dt>
                    <dd>{String(detail.asset || 'USDT').toUpperCase()}</dd>
                  </div>
                  <div>
                    <dt>Network</dt>
                    <dd>{String(detail.network || 'TRC20').toUpperCase()}</dd>
                  </div>
                  <div>
                    <dt>Status</dt>
                    <dd>
                      <StatusBadge status={String(detail.status || '')} />
                    </dd>
                  </div>
                  <div>
                    <dt>Submitted</dt>
                    <dd>
                      {detail.created_at ? new Date(String(detail.created_at)).toLocaleString() : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt>Reviewed</dt>
                    <dd>
                      {detail.reviewed_at ? new Date(String(detail.reviewed_at)).toLocaleString() : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt>Completed</dt>
                    <dd>
                      {detail.completed_at
                        ? new Date(String(detail.completed_at)).toLocaleString()
                        : '—'}
                    </dd>
                  </div>
                  {detail.rejection_reason ? (
                    <div>
                      <dt>Rejection reason</dt>
                      <dd>{String(detail.rejection_reason)}</dd>
                    </div>
                  ) : null}
                  {detail.provider_invoice_id ? (
                    <div>
                      <dt>Provider invoice</dt>
                      <dd>{String(detail.provider_invoice_id)}</dd>
                    </div>
                  ) : null}
                </dl>
                {timeline.length > 0 ? (
                  <div className="cp-deposits__timeline">
                    <h3>Timeline</h3>
                    <ol>
                      {timeline.map((ev, i) => (
                        <li key={`${ev.timestamp}-${i}`}>
                          <strong>{ev.event_type || 'Event'}</strong>
                          <span>{ev.timestamp ? new Date(ev.timestamp).toLocaleString() : '—'}</span>
                          {ev.description ? <p>{ev.description}</p> : null}
                        </li>
                      ))}
                    </ol>
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        open={confirmKind === 'uniwire'}
        title="Confirm Crypto Deposit"
        confirmLabel="Create Invoice"
        cancelLabel="Cancel"
        busyLabel="Creating invoice…"
        busy={busy && confirmKind === 'uniwire'}
        onCancel={() => {
          if (!busy) setConfirmKind(null)
        }}
        onConfirm={() => {
          void executeUniwireInvoice()
        }}
        body={
          <>
            <dl className="ap-dialog__facts">
              <div>
                <dt>Trading Account</dt>
                <dd>
                  {selected
                    ? `MT5 ${maskLogin(selected.mt5Login)} · ${tradingServerLabel(selected.mode, selected.server)}`
                    : '—'}
                </dd>
              </div>
              <div>
                <dt>Amount</dt>
                <dd>${uniwireAmount} USD</dd>
              </div>
              <div>
                <dt>Network</dt>
                <dd>{(uniwireProvider?.network || 'TRC20').toUpperCase()}</dd>
              </div>
              <div>
                <dt>Provider</dt>
                <dd>Uniwire · {(uniwireProvider?.asset || 'USDT').toUpperCase()}</dd>
              </div>
            </dl>
            <p className="ap-dialog__warn">
              Confirm the amount and network carefully. Once created, send only the quoted USDT to the
              invoice address. Incorrect network transfers cannot be recovered by Perfect Predict.
            </p>
          </>
        }
      />
    </section>
  )
}
